'use client'

import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { Button } from '@/components/ui/button'
import { BackButton } from '@/components/ui/BackButton'
import { useToast } from '@/components/ui/use-toast'
import { ErrorDisplay } from '@/components/ui/error-display'
import { useCreateShippingRate, type CreateShippingRateInput } from '@/lib/api/shippingRates'
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils'
import { useStoreNavigate } from '@/hooks/use-app-navigate'
import { ShippingRateForm } from '@/components/form/shippingRate/shipping-rate-form'
import { createShippingRateSchema, type CreateShippingRateFormData } from '@/components/form/shippingRate/validation'

const DEFAULT_VALUES: CreateShippingRateFormData = {
    shippingRateName: '',
    description: '',
    icon: undefined,
    amount: {},
    isFree: false,
    isPickupPoint: false,
    highlighted: false,
    shippingRateMinAmount: null,
    shippingRateMaxAmount: null,
    shippingRateMinWeight: null,
    shippingRateMaxWeight: null,
    countryCodes: [],
    pickupPointCarriers: null,
}

/**
 * Create shipping rate page
 * Form for creating a new shipping rate
 */
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(CreateShippingRatePage, PERMISSIONS.STORES_MANAGE);

function CreateShippingRatePage() {
    const { t } = useTranslation('shippings')
    const { t: tCommon } = useTranslation('common')
    const { storeRoutes, navigate, id: storeId } = useStoreNavigate()
    const { toast } = useToast()

    const createShippingRateMutation = useCreateShippingRate()
    const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null)

    const methods = useForm<CreateShippingRateFormData>({
        defaultValues: DEFAULT_VALUES,
        resolver: zodResolver(createShippingRateSchema),
        mode: 'onBlur',
    })

    const {
        handleSubmit,
        formState: { isSubmitting },
    } = methods

    const handleCreateShippingRate = async (data: CreateShippingRateFormData) => {
        try {
            setFormError(null)

            const input = {
                shippingRateName: data.shippingRateName,
                description: data.description || undefined,
                icon: data.icon || undefined,
                amount: data.amount,
                isFree: data.isFree ?? false,
                isPickupPoint: data.isPickupPoint ?? false,
                highlighted: data.highlighted ?? false,
                shippingRateMinAmount: data.shippingRateMinAmount || null,
                shippingRateMaxAmount: data.shippingRateMaxAmount || null,
                shippingRateMinWeight: data.shippingRateMinWeight || null,
                shippingRateMaxWeight: data.shippingRateMaxWeight || null,
                countryCodes: data.countryCodes ?? [],
                pickupPointCarriers: data.pickupPointCarriers || null,
                storeId,
            } as CreateShippingRateInput

            await createShippingRateMutation.mutateAsync(input)

            toast({
                title: tCommon('success'),
                description: t('createSuccess'),
            })

            navigate(storeRoutes.SHIPPINGS())
        } catch (error) {
            const parsedError = parseTRPCError(error)
            setFormError(parsedError)

            if (!parsedError.isValidationError) {
                toast({
                    title: tCommon('error'),
                    description: parsedError.message,
                    variant: 'destructive',
                })
            }
        }
    }

    return (
        <div className="bg-background min-h-screen">
            <div className="flex flex-col space-y-4 sm:space-y-6">
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(handleCreateShippingRate)}>
                        {/* Header */}
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex items-center gap-4">
                                <BackButton to={storeRoutes.SHIPPINGS()} />
                                <div>
                                    <h1 className="text-2xl font-bold tracking-tight">{t('createShippingRate')}</h1>
                                    <p className="text-muted-foreground mt-1 text-sm">{t('createDescription')}</p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => navigate(storeRoutes.SHIPPINGS())}
                                    disabled={isSubmitting}
                                >
                                    {tCommon('cancel')}
                                </Button>
                                <Button
                                    type="submit"
                                    size="sm"
                                    disabled={isSubmitting || createShippingRateMutation.isPending}
                                >
                                    {isSubmitting || createShippingRateMutation.isPending
                                        ? t('creating')
                                        : t('createShippingRate')}
                                </Button>
                            </div>
                        </div>

                        {/* Error Display */}
                        {formError && <ErrorDisplay error={formError} onDismiss={() => setFormError(null)} />}

                        {/* Main Content */}
                        <div className="mt-6">
                            <ShippingRateForm />
                        </div>
                    </form>
                </FormProvider>
            </div>
        </div>
    )
}

